import { useState, useEffect, useRef, useCallback } from 'react';
import { isGameFinished } from '../utils/constants';

// Convert the NFL feed's "YYYY-MM-DD HH:MM:SS AM|PM TZ" string to an ISO 8601
// datetime that JS Date can parse. Returns null when the input is missing or
// unrecognised — callers should fall through to the raw fields.
const nflEstOffsets = { EST: '-05:00', EDT: '-04:00' };
const toIsoCommenceTime = (startTimeEST, date) => {
  if (typeof startTimeEST === 'string') {
    const m = startTimeEST.match(/^(\d{4}-\d{2}-\d{2})\s+(\d{1,2}):(\d{2}):(\d{2})\s+(AM|PM)\s+([A-Z]{3,4})/i);
    if (m) {
      const [, dPart, hStr, min, sec, ampm, tz] = m;
      let hour = parseInt(hStr, 10);
      const upperAmpm = ampm.toUpperCase();
      if (upperAmpm === 'PM' && hour !== 12) hour += 12;
      if (upperAmpm === 'AM' && hour === 12) hour = 0;
      const offset = nflEstOffsets[tz.toUpperCase()] || '-05:00';
      return `${dPart}T${String(hour).padStart(2, '0')}:${min}:${sec}${offset}`;
    }
    // Already ISO-shaped (or otherwise parseable) — keep as-is.
    if (!Number.isNaN(new Date(startTimeEST).getTime())) return startTimeEST;
  }
  // Last resort: use just the date so day-of-week logic still lines up.
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return `${date}T00:00:00-05:00`;
  }
  return null;
};

const sortGamesByStatus = (games) => {
  return [...games].sort((a, b) => {
    // Move games with status 'Final' or 'Game Over' to the bottom
    if (isGameFinished(a.status) && !isGameFinished(b.status)) {
      return 1;
    }
    if (!isGameFinished(a.status) && isGameFinished(b.status)) {
      return -1;
    }
    // Keep original order for non-finished games
    return 0;
  });
};

export const useNflGames = (selectedSport, selectedWeek, isPlayoffs = false) => {
  const [games, setGames] = useState([]);
  const [gamesMetadata, setGamesMetadata] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);

  const fetchGamesForWeek = async (week = 'today', silent = false, playoffs = false) => {
    if (!silent) {
      setIsLoading(true);
    }
    setError(null);

    try {
      // Use different endpoint for playoffs vs regular season
      const endpoint = playoffs
        ? `/api/games/nfl/schedule/post-season/week/${week}`
        : `/api/games/nfl/schedule/week/${week}`;
      const response = await fetch(endpoint);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        // Transform the data for football-specific structure
        const transformedGames = data.games?.map(game => ({
          gameId: game.gameId, // Use proper field name for football
          gameIDmlb: game.gameId, // Keep for compatibility with shared code
          away: game.away,
          home: game.home,
          awayLogo: game.awayLogo,
          homeLogo: game.homeLogo,
          awayTeamFull: game.awayTeamFull,
          homeTeamFull: game.homeTeamFull,
          awayScore: game.awayScore, // Include scores for finished/live games
          homeScore: game.homeScore, // Include scores for finished/live games
          gameTime: game.startTimeEST || game.gameTime || game.time,
          status: game.status || 'Scheduled',
          gameDate: game.date,
          startTimeEST: game.startTimeEST, // Include EST time for proper date display
          // ISO 8601 form of startTimeEST — parseable by `new Date(...)` so
          // shared formatters (formatGameDayPrefix, getDayOfWeek) can apply
          // the standard convention: today = time only, within 7 days = day
          // of week + time, > 7 days = weekday + M/D.
          commence_time: toIsoCommenceTime(game.startTimeEST, game.date),
          sport: 'nfl',
          week: game.week || week,
          venue: game.venue,
          network: game.network,
          period: game.period, // Quarter/period for live games
          clock: game.clock, // Time remaining for live games
          trial: game.trial // Include trial flag for bonus free games
        })) || [];
        
        // Sort games by status - move finished games to bottom
        const sortedGames = sortGamesByStatus(transformedGames);
        
        setGames(sortedGames);
        setGamesMetadata({
          success: true,
          sport: 'nfl',
          week: data.week || week,
          total: transformedGames.length,
          date: data.date,
          lastUpdate: new Date().toISOString()
        });
      } else {
        throw new Error(data.message || 'Failed to fetch games');
      }
    } catch (err) {
      console.error('Error fetching NFL games:', err);
      setError(err.message);
      // On a silent poll, keep whatever games we already have on screen. Mobile
      // browsers throttle background tabs and often kill in-flight fetches; if
      // we wiped state here the App-level "Loading games..." overlay would show
      // when the user returns to the tab, forcing a manual refresh.
      if (!silent) {
        setGames([]);
        setGamesMetadata({
          success: false,
          error: err.message,
          sport: 'nfl'
        });
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (selectedSport === 'nfl') {
      // Wait until the NFL calendar hook has resolved the current week before
      // firing. Previously we defaulted to weekParam='today' when selectedWeek
      // was still null, which raced against the calendar: if that speculative
      // fetch errored, users saw an "Unable to load games" flash for the
      // second or two until the follow-up week-specific fetch succeeded.
      if (!selectedWeek) return;
      const weekParam = selectedWeek;
      fetchGamesForWeek(weekParam, false, isPlayoffs);

      // Set up polling interval (every 30 seconds)
      intervalRef.current = setInterval(() => {
        fetchGamesForWeek(weekParam, true, isPlayoffs); // silent update
      }, 30000);

      // Refetch immediately when the tab returns from the background — matches
      // useGamesData's behavior so mobile users don't get stuck on the loading
      // overlay after switching apps.
      const handleVisibilityChange = () => {
        if (!document.hidden) {
          fetchGamesForWeek(weekParam, true, isPlayoffs);
        }
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      };
    } else {
      // Clear data when switching away from NFL
      setGames([]);
      setGamesMetadata(null);
      setError(null);

      // Clear interval if switching away from NFL
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [selectedSport, selectedWeek, isPlayoffs]);

  const refetchGames = () => {
    if (selectedSport === 'nfl') {
      const weekParam = selectedWeek ? selectedWeek : 'today';
      fetchGamesForWeek(weekParam, false, isPlayoffs);
    }
  };

  return {
    games,
    gamesMetadata,
    isLoading,
    error,
    refetchGames
  };
};